/**
 * PendantMap — Mapbox GL map component for the PalPendant Locator page.
 *
 * Renders an interactive map with markers for each pendant device location.
 * Blue markers = normal, red markers (1.2x) = alerted.
 * Exposes flyTo() and selectMarker() via forwardRef/useImperativeHandle.
 *
 * Props:
 *   locations      — array of { manufacture_uuid, resident_name, latitude, longitude, create_time }
 *   alertedDevices — array of { manufacture_uuid }
 *   tzOffset       — timezone offset string for convertIsoUTCToLocal
 */

import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { convertIsoUTCToLocal } from '../../../../libs.jsx';

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

// ---------------------------------------------------------------------------
// V2 popup styles — injected once into document head
// ---------------------------------------------------------------------------
const POPUP_STYLE_ID = 'pm-mapbox-popup-styles';

function injectPopupStyles() {
    if (document.getElementById(POPUP_STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = POPUP_STYLE_ID;
    style.textContent = `
        .mapboxgl-popup-content {
            background: var(--color-bg-primary, #fff) !important;
            color: var(--color-text-primary, #111827) !important;
            border-radius: 8px !important;
            box-shadow: 0 4px 16px rgba(0,0,0,0.12) !important;
            padding: 0 !important;
            min-width: 220px;
        }
        .mapboxgl-popup-close-button {
            color: var(--color-text-secondary, #6b7280) !important;
            font-size: 18px !important;
            padding: 4px 8px !important;
        }
        .mapboxgl-popup-close-button:hover {
            background: var(--color-bg-secondary, #f9fafb) !important;
            border-radius: 4px;
        }
        .mapboxgl-popup-tip {
            border-top-color: var(--color-bg-primary, #fff) !important;
        }
    `;
    document.head.appendChild(style);
}

// ---------------------------------------------------------------------------
// Build popup HTML
// ---------------------------------------------------------------------------
function buildPopupHTML(location, isAlerted, tzOffset) {
    const timestamp = convertIsoUTCToLocal(location.create_time, tzOffset) || 'N/A';

    return `
        <div class="pm-popup">
            <div class="pm-popup__row">
                <div class="pm-popup__label">Resident</div>
                <div class="pm-popup__value">${location.resident_name || 'Unknown'}</div>
            </div>
            <div class="pm-popup__row">
                <div class="pm-popup__label">Pendant #</div>
                <div class="pm-popup__value">${location.manufacture_uuid}</div>
            </div>
            <div class="pm-popup__row">
                <div class="pm-popup__label">Last Ping</div>
                <div class="pm-popup__value">${timestamp}</div>
            </div>
            ${isAlerted ? '<div class="pm-popup__alert">Alerted Device</div>' : ''}
        </div>
    `;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const PendantMap = forwardRef(({ locations, alertedDevices, tzOffset }, ref) => {
    const mapContainer = useRef(null);
    const map = useRef(null);
    const markers = useRef({});
    const [selectedMarker, setSelectedMarker] = useState(null);

    // ------------------------------------------------------------------
    // Initialize map (once)
    // ------------------------------------------------------------------
    useEffect(() => {
        injectPopupStyles();

        if (map.current) return;

        map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: 'mapbox://styles/mapbox/streets-v12',
            center: [-98.5795, 39.8283],
            zoom: 3.5,
        });

        return () => map.current?.remove();
    }, []);

    // ------------------------------------------------------------------
    // Update markers when locations/alertedDevices change
    // ------------------------------------------------------------------
    useEffect(() => {
        if (!map.current) return;

        // Preserve selection state
        const wasSelected = selectedMarker;
        const wasOpen = wasSelected && markers.current[wasSelected]?.getPopup()?.isOpen();

        // Clear existing markers
        Object.values(markers.current).forEach((marker) => {
            if (marker) {
                const popup = marker.getPopup();
                if (popup) popup.remove();
                marker.remove();
            }
        });
        markers.current = {};

        // Add new markers
        (locations || []).forEach((location) => {
            if (!map.current) return;

            const isAlerted = (alertedDevices || []).some(
                (d) => d.manufacture_uuid === location.manufacture_uuid
            );

            const marker = new mapboxgl.Marker({
                color: isAlerted ? '#e63757' : '#0081C7',
                scale: isAlerted ? 1.2 : 1.0,
            })
                .setLngLat([location.longitude, location.latitude])
                .setPopup(
                    new mapboxgl.Popup({
                        closeButton: true,
                        closeOnClick: false,
                        className: 'pm-custom-popup',
                    }).setHTML(buildPopupHTML(location, isAlerted, tzOffset))
                )
                .addTo(map.current);

            // Close other popups when this marker is clicked
            marker.getElement().addEventListener('click', () => {
                Object.values(markers.current).forEach((otherMarker) => {
                    if (otherMarker && otherMarker !== marker) {
                        const popup = otherMarker.getPopup();
                        if (popup && popup.isOpen()) popup.remove();
                    }
                });
            });

            markers.current[location.manufacture_uuid] = marker;
        });

        // Restore selection state
        if (wasSelected && markers.current[wasSelected]) {
            const marker = markers.current[wasSelected];
            marker.getElement().style.filter = 'brightness(1.2)';
            if (wasOpen) {
                const popup = marker.getPopup();
                if (popup) popup.addTo(map.current);
            }
        }
    }, [locations, alertedDevices, tzOffset]);

    // ------------------------------------------------------------------
    // Imperative API — flyTo, selectMarker
    // ------------------------------------------------------------------
    useImperativeHandle(ref, () => ({
        flyTo: (options) => {
            if (map.current) {
                map.current.flyTo({
                    ...options,
                    duration: 2000,
                    easing: (t) => t * (2 - t),
                    maxDuration: 3000,
                    essential: true,
                });
            }
        },

        selectMarker: (manufacture_uuid) => {
            // Close all existing popups
            Object.values(markers.current).forEach((marker) => {
                if (marker) {
                    const popup = marker.getPopup();
                    if (popup && popup.isOpen()) popup.remove();
                }
            });

            // Clear previous selection highlight
            if (selectedMarker && markers.current[selectedMarker]) {
                markers.current[selectedMarker].getElement().style.filter = 'none';
            }

            setSelectedMarker(manufacture_uuid);

            // Highlight + open popup for selected marker
            const marker = markers.current[manufacture_uuid];
            if (marker && map.current) {
                marker.getElement().style.filter = 'brightness(1.2)';
                const popup = marker.getPopup();
                if (popup) popup.addTo(map.current);
            }
        },
    }));

    // ------------------------------------------------------------------
    // Render
    // ------------------------------------------------------------------
    return (
        <div
            ref={mapContainer}
            style={{
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
            }}
        />
    );
});

export default PendantMap;
